import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Row, Col, Spinner } from "react-bootstrap";
import TabellaLista from '../../../../components/Tabella/TabellaLista';
import { ChipFilter } from '../../../../components/Tabella/ChipFilter';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { sports, bookingSubscriptionExpirationType } from "../../../../components/Util/enum";
import { useNavigate } from 'react-router-dom';
import { formattaPrezzo } from "../../../../components/Util/prezzi";

// Valori dei filtri a chip, come compaiono nella lista.
// La durata "Data fissa" in lista porta anche la data tra parentesi: si confronta l'inizio.
const DURATA_OPTIONS = bookingSubscriptionExpirationType.map((type) => type.label);
const STATO_OPTIONS = ['Bozza', 'Attivo', 'Non attivo'];

export const SubscriptionsTable = () => {

	const navigate = useNavigate();

	const [data_json, setDataJson] = useState(null);
	const [sportFilter, setSportFilter] = useState('');
	const [durataFilter, setDurataFilter] = useState('');
	// Chip a scelta multipla: all'inizio tutti accesi
	const [statoFilter, setStatoFilter] = useState(STATO_OPTIONS);

	// Gli sport sono quelli degli abbonamenti caricati: di solito pochi per struttura.
	const sportOptions = useMemo(
		() => [...new Set((data_json || []).map((row) => row.sportType).filter(Boolean))].sort(),
		[data_json],
	);

	const filteredData = useMemo(
		() =>
			data_json &&
			data_json.filter(
				(row) =>
					(!sportFilter || row.sportType === sportFilter) &&
					(!durataFilter || (row.expiration_type || '').startsWith(durataFilter)) &&
					// Uno stato fuori dai chip (o mancante) resta sempre visibile
					(!STATO_OPTIONS.includes(row.status_label?.label) || statoFilter.includes(row.status_label?.label)),
			),
		[data_json, sportFilter, durataFilter, statoFilter],
	);

	const [modalShowed, setModalShowed] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalText, setModalText] = useState('');

	const [loadingShowed, setLoadingShowed] = useState(false);
	
	// Colonna "Prezzo soci/tesserati": visibile solo per ASD/SSD (matrice regole clubNature)
	let clubNatureFromSession = null;
	try { clubNatureFromSession = JSON.parse(sessionStorage.getItem('userDetails'))?.clubNature; } catch (e) { clubNatureFromSession = null; }
	const showSociTesseratiColumn = clubNatureFromSession === 'ASD' || clubNatureFromSession === 'SSD';

	const data_column = [
		{
			Header: 'Nome',
			Footer: 'Nome',
			accessor: 'name',
		},
		{
			Header: 'Sport',
			Footer: 'Sport',
			accessor: 'sportType',
			disableGlobalFilter: true,
		},
		{
			Header: 'Durata',
			Footer: 'Durata',
			accessor: 'expiration_type',
			disableGlobalFilter: true,
		},
		{
			Header: 'Prezzo standard',
			Footer: 'Prezzo standard',
			accessor: 'standard_price',
			disableGlobalFilter: true,
		},
		...(showSociTesseratiColumn ? [{
			Header: 'Prezzo soci/tesserati',
			Footer: 'Prezzo soci/tesserati',
			accessor: 'federated_price',
			disableGlobalFilter: true,
		}] : []),
		{
			Header: 'N. Vendite',
			Footer: 'N. Vendite',
			accessor: 'purchasesCount',
			disableGlobalFilter: true,
			Cell: ({ value }) => {
				return value || 0;
			},
			disableFilters: true,
		},
		{
			Header: 'Stato',
			Footer: 'Stato',
			accessor: 'status_label',
			disableGlobalFilter: true,
			Cell: ({ value }) => {
				return (
					<div className="d-flex align-items-center">
						<i className={`fa fa-circle me-1 ${value?value.class:''}`}></i>
						{value?value.label:''}
					</div>
				)
			},
			disableFilters: true,
		},
	];

	function openDetail(row) {
		if(row.status === 'DRAFT') {
			navigate('/struttura/booking/subscriptionsedit/'+row.id);
		} else {
			navigate('/struttura/booking/subscriptionsview/'+row.id);
		}
	}

	useEffect(() => {
        const fetchData = async () => {
            try {
                const tokenDetailsString = sessionStorage.getItem('userDetails');
                if (tokenDetailsString) {
                    const tokenDetails = JSON.parse(tokenDetailsString);
                    let token = tokenDetails.idToken;
                    const apiClient = new MyApiClient();

					setLoadingShowed(true);
					const [bookingSubscriptions] = await Promise.all([
                        apiClient.getBookingSubscriptions(token)
                    ]);
					setLoadingShowed(false);

					if(bookingSubscriptions) {
						let data = [];
						bookingSubscriptions.forEach(element => {

							let status = null;
							if(element.status === 'DRAFT') {
								status = {
									"label": "Bozza",
									"class": "text-warning"
								};
							} else if(element.status === 'PURCHASABLE') {
								status = {
									"label": "Attivo",
									"class": "text-success"
								};
							} else if(element.status === 'CANCELLED') {
								status = {
									"label": "Non attivo",
									"class": "text-primary"
								};
							}

							let sportLabel = '';
							sports.forEach(element2 => {
								if(element2.value === element.sportType) {
									sportLabel = element2.label;
								}
							});

							let expiration_typeLabel = '';
							bookingSubscriptionExpirationType.forEach(element2 => {
								if(element2.value === element.expiration_type) {
									expiration_typeLabel = element2.label;
								}
							});

							if(element.expiration_type === 'FIXED_DATE' && element.expiration_date) {
								const d = element.expiration_date;
								const formattedDate = d.substring(8, 10) + '/' + d.substring(5, 7) + '/' + d.substring(0, 4);
								expiration_typeLabel = expiration_typeLabel + ' (' + formattedDate + ')';
							}

							let row = {
								"id": element.id,
								"name": element.name,
								"sportType": sportLabel,
								"expiration_type": expiration_typeLabel,
								"standard_price": formattaPrezzo(element.standard_price, { conZero: false }),
								"federated_price": formattaPrezzo(element.federated_price, { conZero: false }),
								"status": element.status,
								"status_label": status,
								"purchasesCount": element.purchasesCount,
							};
							data.push(row);
							
						});
						setDataJson(data);
					}
                }
            } catch (error) {
                // Elenco non caricato: lo si dice, invece di mostrare una lista vuota
                setLoadingShowed(false);
                console.error("Error fetching data:", error);
                setModalTitle("Impossibile caricare gli abbonamenti");
                setModalText("Il server non ha risposto alla richiesta. Riprova fra poco.");
                setModalShowed(true);
            }
        };

        fetchData();

    }, []);

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
				<Spinner animation="border" role="status">
					<span className="sr-only">Loading...</span>
				</Spinner>
				<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={modalShowed} centered>
                <Modal.Header>
                    <Modal.Title>{modalTitle}</Modal.Title>
                    <Button onClick={() => setModalShowed(false)} variant="" className="btn-close">
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    <Row><Col className="col-xl-64">{modalText}</Col></Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>
                        Chiudi
                    </Button>
                </Modal.Footer>
            </Modal>
			
			<TabellaLista
				pageList={true}
				cardTitle={'Abbonamenti'}
				hasSearch={true}
				searchPlaceholder={'Cerca per nome'}
				hasFilter={false}
				filterControl={
					<div className="sid-chip-filters">
						{sportOptions.length > 1 && <ChipFilter label="Sport" options={sportOptions} value={sportFilter} onChange={setSportFilter} asDropdown />}
						<ChipFilter label="Durata" options={DURATA_OPTIONS} value={durataFilter} onChange={setDurataFilter} />
						<ChipFilter label="Stato" options={STATO_OPTIONS} value={statoFilter} onChange={setStatoFilter} multiple />
					</div>
				}
				data_column={data_column}
				data_json={filteredData}
				openColumn={'name'}
				openRow={(row) => openDetail(row)}
				hasAddMore={true}
				linkAddMore={'/struttura/booking/subscriptions/new'}
				labelAddMore={'Aggiungi'}
				sort_by={'name'}
                sort_type={'asc'}
			/>
		</>
	)
}
export default SubscriptionsTable;